import { Box, Flex, Text, Divider, Heading, MenuItem, SimpleGrid, useMediaQuery } from '@chakra-ui/react';

import styled from '@emotion/styled/macro';

import { FormattedMessage, useIntl } from 'react-intl';

import Tooltip from '../utils/Tooltip';
import CircularIconButton from '../utils/CircularIconButton';
import AccordionIconButton from '../utils/AccordionIconButton';

import InputFormikField from '../forms/InputFormikField';

import SubItemActionsMenu from '../../components/itemData/SubItemActionsMenu';

import { ReactComponent as EditIcon } from '../../icons/edit-circled.svg';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';
import { ReactComponent as AddItemIcon } from '../../icons/item-icons/add-item.svg';

const SubItemHeader = ({
    actionBtn,
    actionBtnMobile,
    isExpanded,
    isEditing,
    isValidated,
    isEditVisible,
    inputName,
    subItemHeading,
    itemDescription,
    onAddSubItemClick,
    isAddDisabled,
    isEditMode,
    onEditClick,
}) => {
    const intl = useIntl();
    const [isDesktop] = useMediaQuery('(min-width: 992px)');

    return (
        <>
            <CustomSimpleGrid columns={2} isExpanded={isExpanded}>
                <Flex>
                    <AccordionIconButton isEditing={isEditing}>
                        {isExpanded ? <CustomChevronUpIcon /> : <CustomChevronDownIcon />}
                    </AccordionIconButton>

                    <Box my={3}>
                        <Text size="xs" mb="2px">
                            {subItemHeading}
                        </Text>

                        {!isEditMode ? (
                            <Heading as="h4" variant="h4">
                                {itemDescription}
                            </Heading>
                        ) : (
                            <InputFormikField
                                name={inputName}
                                validate={isValidated}
                                width="300px"
                                type="text"
                                isFastField
                            />
                        )}
                    </Box>

                    {isEditVisible && (
                        <Flex align="center" ml={3}>
                            <Tooltip label={intl.formatMessage({ id: 'common_edit_btn_tooltip_and_label' })}>
                                <CircularIconButton
                                    aria-label={intl.formatMessage({ id: 'common_edit_btn_tooltip_and_label' })}
                                    leftIcon={<EditIcon />}
                                    isEditing={isEditMode}
                                    onClick={() => onEditClick(!isEditMode)}
                                />
                            </Tooltip>
                        </Flex>
                    )}
                </Flex>

                <Flex justify="flex-end" align="center" pr={6}>
                    {!isDesktop ? (
                        <SubItemActionsMenu
                            menuListItems={
                                <>
                                    {actionBtnMobile}

                                    <MenuItem
                                        key="create-btn"
                                        textTransform="capitalize"
                                        onClick={onAddSubItemClick}
                                        isDisabled={isAddDisabled}
                                    >
                                        <FormattedMessage id="common_add" />
                                    </MenuItem>
                                </>
                            }
                        />
                    ) : (
                        <>
                            {actionBtn}

                            <Divider orientation="vertical" height="68px" />

                            <Box display="inline-block" mx={6}>
                                <Tooltip label={intl.formatMessage({ id: 'common_add_new' })}>
                                    <StyledCircularIconButton
                                        isDisabled={isAddDisabled}
                                        aria-label={intl.formatMessage({ id: 'common_add_new' })}
                                        icon={<AddItemIcon />}
                                        onClick={onAddSubItemClick}
                                    />
                                </Tooltip>
                            </Box>
                        </>
                    )}
                </Flex>
            </CustomSimpleGrid>
        </>
    );
};

const StyledCircularIconButton = styled(CircularIconButton)`
    &:disabled {
        opacity: 0.6;

        &:hover svg path,
        svg path {
            fill: var(--chakra-colors-gray-600);
        }
    }
`;
const CustomSimpleGrid = styled(SimpleGrid, { shouldForwardProp: (prop) => prop !== 'isExpanded' })`
    background: var(--chakra-colors-gray-50);
    border: var(--chakra-borders-1px);
    border-color: var(--chakra-colors-gray-200);
    border-left: var(--chakra-borders-none);
    border-bottom: ${(props) => !props.isExpanded && '1px'};
`;

const CustomChevronDownIcon = styled(ChevronDownIcon)`
    circle {
        fill: var(--chakra-colors-gray-100);
    }

    path {
        fill: var(--chakra-colors-gray-600);
    }

    &:hover {
        path {
            fill: var(--chakra-colors-blue-400);
        }
    }
`;

const CustomChevronUpIcon = styled(CustomChevronDownIcon)`
    transform: rotate(180deg);
`;

export default SubItemHeader;
